import type { ApiErrorMessage } from 'epic/apiErrorEpicHelper';
import type { IntlKey } from 'locales';
import type { Observable, SchedulerLike } from 'rxjs';
import { throwError } from 'rxjs';
import type { AjaxError, AjaxResponse } from 'rxjs/ajax';
import { ajax } from 'rxjs/ajax';
import { catchError, map, timeoutWith } from 'rxjs/operators';
import { getSgmeConfiguration } from 'state/configureStore';
import { HttpTimeoutError } from 'utils/http/httpClient.model';

import type { HttpCall, HttpError, IHttpClient, IHttpConfig, IHttpGetConfig } from './httpClient.model';
import { applyGetMiddlewares, applyRequestMiddlewares, applyResponseErrorMiddlewares } from './httpMiddlewares';

const defaultTechnicalErrorKey: IntlKey = 'quote.errorMessages.TechnicalError';

/**
 * deprecated
 */
export const httpClient: IHttpClient = {
  patch<T>(config: IHttpConfig): Observable<HttpCall<T>> {
    const start = performance.now();
    const httpConfig = { ...config };
    const requestConfig = applyRequestMiddlewares(httpConfig);

    return mapResultWithResponseMiddlewares<T>(
      requestConfig,
      start,
    )(ajax.patch(requestConfig.url, requestConfig.body, requestConfig.headers));
  },
  post<T>(config: IHttpConfig, scheduler?: SchedulerLike): Observable<HttpCall<T>> {
    const start = performance.now();
    const requestConfig = applyRequestMiddlewares(config);

    return mapResultWithResponseMiddlewares<T>(
      requestConfig,
      start,
      scheduler,
    )(ajax.post(requestConfig.url, requestConfig.body, requestConfig.headers));
  },
  postRAW(config: IHttpGetConfig, scheduler?: SchedulerLike): Observable<HttpCall<string>> {
    const start = performance.now();
    const httpConfig = { ...config, method: 'POST', responseType: 'text' };
    const requestConfig = applyRequestMiddlewares(httpConfig);

    return mapResultWithResponseMiddlewares<string>(requestConfig, start, scheduler)(ajax(requestConfig));
  },
  postArrayBuffer(config: IHttpGetConfig): Observable<HttpCall<ArrayBuffer>> {
    const start = performance.now();
    const httpConfig = { ...config, method: 'POST', responseType: 'arraybuffer' };
    const requestConfig = applyRequestMiddlewares(httpConfig);

    return mapResultWithResponseMiddlewares<ArrayBuffer>(requestConfig, start)(ajax(requestConfig));
  },
  postBlob(config: IHttpGetConfig, type?: string): Observable<HttpCall<Blob>> {
    const response$ = this.postArrayBuffer(config);
    return response$.pipe(
      map(response => ({
        ...response,
        payload: new Blob([response.payload], { type }),
      })),
    );
  },
  put<T>(config: IHttpConfig): Observable<HttpCall<T>> {
    const start = performance.now();
    const httpConfig = { ...config };
    const requestConfig = applyRequestMiddlewares(httpConfig);

    return mapResultWithResponseMiddlewares<T>(
      requestConfig,
      start,
    )(ajax.put(requestConfig.url, requestConfig.body, requestConfig.headers));
  },
  delete<T>(config: IHttpConfig): Observable<HttpCall<T>> {
    const start = performance.now();
    const httpConfig = { ...config, method: 'DELETE' };
    const requestConfig = applyRequestMiddlewares(httpConfig);

    return mapResultWithResponseMiddlewares<T>(
      requestConfig,
      start,
    )(ajax.delete(requestConfig.url, requestConfig.headers));
  },
  getJSON<T>(config: IHttpGetConfig): Observable<HttpCall<T>> {
    const start = performance.now();
    const requestConfig = applyGetMiddlewares(config);

    return mapResultWithResponseMiddlewares<T>(
      requestConfig,
      start,
    )(ajax.get(requestConfig.url, requestConfig.headers));
  },
  getRAW(config: IHttpGetConfig): Observable<HttpCall<string>> {
    const start = performance.now();
    const httpConfig = { ...config, method: 'GET', responseType: 'text' };
    const requestConfig = applyGetMiddlewares(httpConfig);

    return mapResultWithResponseMiddlewares<string>(requestConfig, start)(ajax(requestConfig));
  },
  getArrayBuffer(config: IHttpGetConfig): Observable<HttpCall<ArrayBuffer>> {
    const start = performance.now();
    const httpConfig = { ...config, method: 'GET', responseType: 'arraybuffer' };
    const requestConfig = applyGetMiddlewares(httpConfig);

    return mapResultWithResponseMiddlewares<ArrayBuffer>(requestConfig, start)(ajax(requestConfig));
  },
  getBlob(config: IHttpGetConfig, type?: string): Observable<HttpCall<Blob>> {
    const response$ = this.getArrayBuffer(config);
    return response$.pipe(
      map(response => ({
        ...response,
        payload: new Blob([response.payload], { type }),
      })),
    );
  },
};

function applyTimeout(config: IHttpConfig, scheduler?: SchedulerLike) {
  return function operator<T>(source$: Observable<T>): Observable<T> {
    if (config.timeOut) {
      return source$.pipe(
        timeoutWith(
          config.timeOut,
          throwError(
            new HttpTimeoutError(config.timeOut, config.timeOutErrorMessage, config.headers?.['X-Correlation-ID']),
          ),
          scheduler,
        ),
      );
    }
    return source$;
  };
}

function getErrorMessage(error: AjaxError): ApiErrorMessage {
  const payload = error.response?.payload || error.response;
  const isCensored = isBadRequest(error) || typeof payload !== 'string';
  return isBackendTechnicalErrorPayload(payload)
    ? payload
    : {
        message: isCensored ? defaultTechnicalErrorKey : payload,
      };
}

function isBadRequest(error: AjaxError) {
  if (error.status === 400) {
    return true;
  }
  return false;
}

function isBackendTechnicalErrorPayload(payload: any): payload is ApiErrorMessage {
  if (!payload) {
    return false;
  }
  const backendTechnicalErrorPayload = <ApiErrorMessage>{
    message: payload.message,
    newVersion: payload.newVersion,
  };
  return backendTechnicalErrorPayload.message !== undefined;
}

function getContentDispositionFileName(xhr: XMLHttpRequest): string | undefined {
  if (!xhr.getAllResponseHeaders().includes('content-disposition')) {
    return undefined;
  }

  const header = xhr.getResponseHeader('content-disposition');
  const parts = header?.split('; ');
  const matchedPart = parts?.find(p => p.includes('filename='));
  return matchedPart?.replace('filename=', '');
}

function successResponseMapper<T>(ajaxResponse: AjaxResponse<T>, start: number): HttpCall<T> {
  const end = performance.now();
  const payload =
    !ajaxResponse.response || ajaxResponse.response === '' ? (null as unknown as T) : (ajaxResponse.response as T);
  const attachmentName = getContentDispositionFileName(ajaxResponse.xhr);

  return {
    payload,
    timeTaken: end - start,
    attachmentName,
  };
}

function mapResultWithResponseMiddlewares<T>(config: IHttpConfig, start: number, scheduler?: SchedulerLike) {
  return (input: Observable<AjaxResponse<T>>) =>
    input.pipe(
      catchError((err: AjaxError) => {
        applyResponseErrorMiddlewares()(err);
        if (config.skipErrorMapping) {
          throw err;
        }
        const end = performance.now();
        const errorContent = getErrorMessage(err);
        const traceIdHeaderName = getSgmeConfiguration().traceIdHeaderName;
        throw <HttpError>{
          name: err.name,
          status: err.status,
          correlationId: config.headers?.['X-Correlation-ID'],
          url: config.url,
          message: errorContent.message,
          newVersion: errorContent.newVersion,
          duration: end - start,
          traceId: traceIdHeaderName ? (err.xhr?.getResponseHeader(traceIdHeaderName) ?? undefined) : undefined,
          response: typeof err.response === 'object' ? err.response : { message: err.response },
        };
      }),
      applyTimeout(config, scheduler),
      map(ajaxResponse => successResponseMapper<T>(ajaxResponse, start)),
    );
}
